import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, exhaustMap, tap, withLatestFrom, filter } from 'rxjs/operators';
import * as ValidationActions from './validation.actions';
import { ValidationService } from '../../services/validation.service';
import { SetSnackbar } from 'src/app/store/app.actions';
import { Router } from '@angular/router';
import { Location } from '@angular/common';
import { getSuspectedAndVerified, getVerifiedTasks } from './validation.selectors';
import { IDiscrepancy } from 'src/app/discrepancies/models/discrepancies.model';

@Injectable({
  providedIn: 'root'
})
export class ValidationEffects {
  readonly router = inject(Router);
  readonly store = inject(Store);
  readonly location = inject(Location);
  readonly actions$ = inject(Actions);
  readonly validationService = inject(ValidationService);

  getOutputChecks$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ValidationActions.GetOutputChecks),
      switchMap(({ projectID }) =>
        this.validationService.getOutputChecks(projectID).pipe(
          map((checks) => ValidationActions.GetOutputChecksSuccess({ checks })),
          catchError((err: Error) => of(ValidationActions.GetOutputChecksFailed({ payload: err })))
        )
      )
    );
  });

  downloadFile$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ValidationActions.DownloadFile),
      switchMap(({ projectID, fileID, compressed }) =>
        this.validationService.downloadFile(projectID, fileID, compressed).pipe(
          switchMap(() => [
            ValidationActions.DownloadFileSuccess(),
            SetSnackbar({ payload: { message: 'File downloaded' } })
          ]),
          catchError((err: Error) => of(ValidationActions.DownloadFileFailed({ payload: err })))
        )
      )
    );
  });

  updateTaskOutcome$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ValidationActions.UpdateTaskOutcome),
      withLatestFrom(this.store.select(getSuspectedAndVerified)),
      exhaustMap(([{ projectID, taskID, outcome }, suspectedAndVerified]) =>
        this.validationService.updateTaskOutcome(projectID, taskID, outcome).pipe(
          switchMap(({ task, documentOutcomes }) => [
            ValidationActions.UpdateTaskOutcomeSuccess({ task: updateTask(task, suspectedAndVerified), documentOutcomes }),
            SetSnackbar({ payload: { message: 'Task outcome updated successfully' } })
          ]),
          catchError((err: Error) => of(ValidationActions.UpdateTaskOutcomeFailed({ payload: err })))
        )
      )
    );
  });

  updateDocumentOutcome$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ValidationActions.UpdateDocumentOutcome),
      withLatestFrom(this.store.select(getSuspectedAndVerified)),
      exhaustMap(([{ projectID, documentID, outcome, checkKey }, suspectedAndVerified]) =>
        this.validationService.updateDocumentOutcome(projectID, documentID, checkKey, outcome).pipe(
          switchMap(({ tasks, documentOutcomes }) => [
            ValidationActions.UpdateDocumentOutcomeSuccess({
              tasks: tasks.map((task) => updateTask(task, suspectedAndVerified)).filter(Boolean),
              documentOutcomes
            }),
            SetSnackbar({ payload: { message: 'File outcome updated successfully' } })
          ]),
          catchError((err: Error) => of(ValidationActions.UpdateDocumentOutcomeFailed({ payload: err })))
        )
      )
    );
  });

  updateDocumentApplicability$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ValidationActions.UpdateDocumentApplicability),
      withLatestFrom(this.store.select(getSuspectedAndVerified)),
      exhaustMap(([{ projectID, documentID, applicable, checkKey }, suspectedAndVerified]) =>
        this.validationService.updateDocumentApplicability(projectID, documentID, checkKey, applicable).pipe(
          switchMap(({ documentApplicability, tasks, documentOutcomes }) => [
            ValidationActions.UpdateDocumentApplicabilitySuccess({
              documentApplicability,
              tasks: tasks?.map((task) => updateTask(task, suspectedAndVerified)).filter(Boolean),
              documentOutcomes
            }),
            SetSnackbar({ payload: { message: 'File applicability updated successfully' } })
          ]),
          catchError((err: Error) => of(ValidationActions.UpdateDocumentApplicabilityFailed({ payload: err })))
        )
      )
    );
  });

  updateTlfDetails$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ValidationActions.UpdateTlfDetails),
      map(({ projectID, tlfID, title, number, footnotes, protocol }) => ({
        projectID,
        tlfID,
        title,
        number,
        footnotes,
        protocol
      })),
      switchMap(({ projectID, tlfID, title, number, footnotes, protocol }) =>
        this.validationService.updateTlfDetails(projectID, tlfID, { title, number, footnotes, protocol }).pipe(
          map(({ tlf }) => ValidationActions.UpdateTlfDetailsSuccess({ tlf })),
          catchError(({ error }) => of(ValidationActions.UpdateTlfDetailsFailed({ error })))
        )
      )
    );
  });

  createTask$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ValidationActions.CreateTask),
      switchMap(({ projectID, task, repeatLogic }) =>
        this.validationService.createTask(projectID, task, repeatLogic).pipe(
          switchMap(({ discrepancies, documentOutcomes }) => [
            ValidationActions.CreateTaskSuccess({ discrepancies, documentOutcomes }),
            SetSnackbar({ payload: { message: 'Task created successfully' } })
          ]),
          catchError((err: Error) => of(ValidationActions.CreateTaskFailed({ payload: err })))
        )
      )
    );
  });

  addAdminRemarks$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ValidationActions.PatchAdminRemarks),
      switchMap(({ projectID, documentIDs, adminRemark }) =>
        this.validationService.addAdminRemarks(projectID, documentIDs, adminRemark).pipe(
          switchMap(({ files }) => [
            ValidationActions.PatchAdminRemarksSuccess({ files }),
            SetSnackbar({ payload: { message: 'Admin remarks patched successfully' } })
          ]),
          catchError((err: Error) => of(ValidationActions.PatchAdminRemarksFailed({ payload: err })))
        )
      )
    );
  });

  deletedTask$: Observable<Action> = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(ValidationActions.UpdateTaskOutcomeSuccess),
        tap(({ task }) => {
          if (['VERIFIED', 'SUSPECTED'].includes(task.outcome)) return;
          const currentUrl = this.location.path();
          if (!currentUrl.includes('designer')) return;
          const relativeUrl = currentUrl.substring(0, currentUrl.lastIndexOf('/')) + `/new`;
          this.router.navigate([relativeUrl]);
        })
      );
    },
    { dispatch: false }
  );

  updateTask$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ValidationActions.UpdateTask),
      switchMap(({ projectID, task }) =>
        this.validationService.updateTask(projectID, task).pipe(
          switchMap(({ discrepancy, documentOutcomes }) => [
            ValidationActions.UpdateTaskSuccess({ discrepancy, documentOutcomes }),
            SetSnackbar({ payload: { message: 'Task updated successfully' } })
          ]),
          catchError((err: Error) => of(ValidationActions.UpdateTaskFailed({ payload: err })))
        )
      )
    );
  });

  getVerifiedTasks$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ValidationActions.GetVerifiedTasks),
      withLatestFrom(this.store.select(getVerifiedTasks)),
      filter(([{ documentName }, tasks]) => !tasks?.[documentName]),
      exhaustMap(([{ projectID, documentID }]) =>
        this.validationService.getVerifiedTasks(projectID, documentID).pipe(
          map(({ tasks, tlfName }) => ValidationActions.GetVerifiedTasksSuccess({ tasks, tlfName })),
          catchError((err: Error) => of(ValidationActions.GetVerifiedTasksFailed({ payload: err })))
        )
      )
    );
  });

  setBulkDocumentApplicability$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ValidationActions.SetBulkDocumentApplicability),
      withLatestFrom(this.store.select(getSuspectedAndVerified)),
      exhaustMap(([{ projectID, data }, suspectedAndVerified]) =>
        this.validationService.setBulkDocumentApplicability(projectID, data).pipe(
          switchMap(({ documentApplicability, tasks, documentOutcomes }) => [
            ValidationActions.SetBulkDocumentApplicabilitySuccess({
              documentApplicability,
              tasks: tasks?.map((task) => updateTask(task, suspectedAndVerified)).filter(Boolean),
              documentOutcomes
            }),
            SetSnackbar({ payload: { message: 'File applicability updated successfully' } })
          ]),
          catchError((err: Error) => of(ValidationActions.SetBulkDocumentApplicabilityFailed({ payload: err })))
        )
      )
    );
  });

  recalculateCounters$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(ValidationActions.RecalculateCounters),
      exhaustMap(({ payload: projectID }) =>
        this.validationService.recalculateCounters(projectID).pipe(
          map(() => SetSnackbar({ payload: { message: 'Recalculate counters success' } })),
          catchError(() => of(SetSnackbar({ payload: { message: 'Recalculate counters failed' } })))
        )
      )
    );
  });
}

const updateTask = (update: Pick<IDiscrepancy, 'id' | 'outcome' | 'details'>, suspectedAndVerified: IDiscrepancy[]) => {
  const task = suspectedAndVerified.find(({ id }) => id === update.id);
  if (!task) return null;
  return { ...task, outcome: update.outcome, details: update.details ?? task.details };
};
